/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useCallback, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { NUMBER_OF_ITEMS_PER_PAGE } from '../../../../Shared/Utilities/utils';
import { ACTIVE_WORK_ORDER_STATUSES, DEFAULT_PAGE_NUMBER, DefaultScheduleName } from '../../../../Shared/Constants/App';

import WorkOrdersApiService from '../../Services/WorkOrdersService';
import { WorkOrder, WorkOrderStatus } from '../../../../API';

import { FacilityInterface } from '../../../Company/Models/Company.Model';
import { ISchedules } from '../../../../Shared/Models/WorkTask.model';

import DateSelection from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import AssociateOnTheJobAccordion, { IEmployees } from './AssociateOnTheJobAccordion';
import { ScreenName } from '../../../../Shared/Components/Common/Accordion/Accordion';

import theme from '../../../../Shared/Themes/theme';
import { LoaderTag } from '../ActionItems/ActionItems.Style';
import { DatePickerWrapper } from '../../../../Shared/Components/Common/GoogleMap/GoogleMapChips.Style';
import { Loader } from '../JobView/JobsView.styles';
import { DropdownWrapper, NoDataTag, Services, TitleWrapper } from '../WorkTasks/JobDetailsTasks.styles';

const isActiveWorkOrder = (workOrder?: { status?: string | null } | null) =>
  ACTIVE_WORK_ORDER_STATUSES.includes(workOrder?.status as WorkOrderStatus);

interface AssociateCardData {
  jobData?: WorkOrder;
  FacilityData?: FacilityInterface;
  jobId: string;
  jobsLoader?: boolean;
  specificDate: any;
  jobStartDate: string;
  jobEndDate: string;
  control: any;
  workOrderResponseData: any;
  futureShiftDates?: string[];
  isPrimeVendor?: boolean;
  parentWoScheduleMasterId?: string;
}
export interface SchedulesByDateQuery {
  workOrderScheduleId: string;
  workOrderId: string;
  woScheduleMasterId: string;
  scheduleDate?: string | null;
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  scheduleType?: string | null;
  dataStatus?: string | null;
  isRescheduled: boolean;
  scheduleSubName: string;
  employees?: IEmployees[];
  scheduleName: string;
}

export interface AllShiftDetailsQuery {
  shiftDetailId: string;
  startTime: string;
  endTime: string;
  workOrderId: string;
  punchInGraceMinutes?: number | null;
  punchOutGraceMinutes?: number | null;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  scheduleName?: string | null;
  scheduleSubName?: string;
  endDateOffset?: number | null;
  workOrder?: {
    __typename: 'ShiftDetailsWorkOrder';
    jobStartDate?: string | null;
    jobEndDate?: string | null;
    actualStartDate?: string | null;
    actualEndDate?: string | null;
  } | null;
  shiftStartDateTime?: string;
  shiftEndDateTime?: string;
}

const AssociateCard = ({
  jobData,
  FacilityData,
  jobId,
  jobsLoader,
  specificDate,
  jobStartDate,
  jobEndDate,
  control,
  workOrderResponseData,
  futureShiftDates,
  isPrimeVendor,
  parentWoScheduleMasterId,
}: AssociateCardData) => {
  const [scheduleList] = useState<ISchedules[]>([]);
  const [primeAccordionID, setPrimeAccordionID] = useState('');
  const [primeExpanded, setPrimeExpanded] = useState<number | false>(0);
  const [subAccordionID, setSubAccordionID] = useState('');
  const [subExpanded, setSubExpanded] = useState<number | false>(0);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<SchedulesByDateQuery[]>([]);
  const [shiftDetails, setShiftDetails] = useState<AllShiftDetailsQuery[]>([]);
  const [scheduleStatus, setScheduleStatus] = useState<string | null>(null);

  const { t } = useTranslation(['assignJob', 'calendar', 'taskRejectionAndRework']);

  const handlePrimeAccordion = (panel: number, id: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setPrimeAccordionID(id);
    setPrimeExpanded(newExpanded ? panel : false);
  };

  const handleSubAccordion = (panel: number, id: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setSubAccordionID(id);
    setSubExpanded(newExpanded ? panel : false);
  };

  const getSchedulesData = useCallback(async () => {
    setIsDataLoading(true);
    try {
      if (isEmpty(workOrderResponseData) || !specificDate) {
        setIsDataLoading(false);
        return;
      }

      // The API returns every work order for the job (e.g. Terminated ones first), so search the whole list
      const parentWorkOrder = workOrderResponseData?.find(isActiveWorkOrder);
      const workOrderId = parentWorkOrder?.workOrderId;
      // Only Accepted/InProgress work orders are sent; only one is supported for now
      const targetWorkOrderId = parentWorkOrder?.subWorkOrders?.length
        ? parentWorkOrder?.subWorkOrders?.find(isActiveWorkOrder)?.workOrderId
        : workOrderId;

      if (isEmpty(targetWorkOrderId)) {
        setSchedules([]);
        setIsDataLoading(false);
        return;
      }

      const scheduleDate = moment(specificDate).format('YYYY-MM-DD');
      let schedulesByDateResponse;
      schedulesByDateResponse = await WorkOrdersApiService.schedulesByDateV2(
        scheduleDate,
        targetWorkOrderId,
        true
      );
      const totalCount = schedulesByDateResponse?.metaData?.totalCount;
      if (totalCount > NUMBER_OF_ITEMS_PER_PAGE) {
        schedulesByDateResponse = await WorkOrdersApiService.schedulesByDateV2(
          scheduleDate,
          targetWorkOrderId,
          true,
          '',
          '',
          '',
          '',
          totalCount,
          DEFAULT_PAGE_NUMBER
        );
      }
      if (schedulesByDateResponse?.data && schedulesByDateResponse?.errors?.length === 0) {
        setSchedules(schedulesByDateResponse?.data);
        setScheduleStatus(schedulesByDateResponse?.data?.[0]?.dataStatus);

        if (schedulesByDateResponse?.data?.[0]) {
          const firstId = schedulesByDateResponse?.data?.[0]?.workOrderScheduleId;
          setPrimeAccordionID(firstId);
          setSubAccordionID(firstId);
        }
      } else {
        setSchedules([]);
      }
      const shiftDetailResponse = await WorkOrdersApiService.getallShiftDetails(workOrderId as string);
      setShiftDetails(shiftDetailResponse?.data);

      setIsDataLoading(false);
    } catch (e) {
      console.log('error', e);
      setIsDataLoading(false);
    }
  }, [jobData, specificDate, workOrderResponseData]);

  const renderPrimeVendorTeamDetails = () => {
    return (
      <>
        <TitleWrapper sx={{ marginTop: '0rem' }}>
          <Services>{t('assignJob:primeVendorTeamDetails')}</Services>
        </TitleWrapper>
        {jobsLoader || isDataLoading ? (
          <Loader>
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          </Loader>
        ) : schedules?.length ? (
          schedules?.map((dataItem) => {
            const shiftData = shiftDetails.filter((data) => data?.shiftDetailId === dataItem?.woScheduleMasterId);
            const scheduleName = shiftData?.[0]?.scheduleName || dataItem?.scheduleName;
            const sectionTitle = !isEmpty(scheduleName) ? scheduleName : DefaultScheduleName;
            const scheduleSubName = !isEmpty(dataItem?.scheduleSubName) ? dataItem?.scheduleSubName : '';
            const employeeCount = dataItem?.employees?.length;
            const employees = dataItem?.employees as IEmployees[];

            return (
              <AssociateOnTheJobAccordion
                scheduleList={scheduleList}
                key={dataItem?.workOrderScheduleId}
                expanded={primeAccordionID === dataItem?.workOrderScheduleId && primeExpanded}
                handleAccordion={(e) => handlePrimeAccordion(e, dataItem?.workOrderScheduleId)}
                accordionObjNew={dataItem}
                screenName={ScreenName.JobDetails}
                jobData={jobData}
                FacilityData={FacilityData}
                jobId={jobId}
                jobsLoader={jobsLoader}
                specificDate={specificDate}
                shiftDetails={shiftDetails}
                isDataLoading={isDataLoading}
                sectionTitle={sectionTitle}
                scheduleSubName={scheduleSubName}
                shiftData={shiftData}
                workOrderResponseData={workOrderResponseData}
                employees={employees}
                employeeCount={employeeCount}
                scheduleStatus={scheduleStatus}
                isPrimeVendor={isPrimeVendor}
                workOrderId={workOrderResponseData?.[0]?.workOrderId}
                woScheduleMasterId={parentWoScheduleMasterId ?? ''}
              />
            );
          })
        ) : (
          <NoDataTag>{t('noData:noSchedulesFound')}</NoDataTag>
        )}
      </>
    );
  };

  const renderTasksData = () => {
    let index = 0;
    return (
      <>
        {isPrimeVendor && <>{renderPrimeVendorTeamDetails()}</>}
        {isPrimeVendor && (
          <TitleWrapper sx={{ marginTop: '0rem' }}>
            <Services>{t('assignJob:subVendorTeamDetails')}</Services>
          </TitleWrapper>
        )}
        {jobsLoader || isDataLoading ? (
          <Loader>
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          </Loader>
        ) : schedules?.length ? (
          schedules?.map((dataItem) => {
            index = index + 1;
            const shiftData = shiftDetails.filter((data) => data?.shiftDetailId === dataItem?.woScheduleMasterId);
            const scheduleName = shiftData?.[0]?.scheduleName || dataItem?.scheduleName;
            const sectionTitle = !isEmpty(scheduleName) ? scheduleName : `Schedule #${index}`;
            const scheduleSubName = !isEmpty(dataItem?.scheduleSubName) ? dataItem?.scheduleSubName : '';
            const employeeCount = dataItem?.employees?.length;
            const employees = dataItem?.employees as IEmployees[];
            return (
              <AssociateOnTheJobAccordion
                scheduleList={scheduleList}
                key={dataItem?.workOrderScheduleId}
                expanded={subAccordionID === dataItem?.workOrderScheduleId && subExpanded}
                handleAccordion={(e) => handleSubAccordion(e, dataItem?.workOrderScheduleId)}
                accordionObjNew={dataItem}
                screenName={ScreenName.JobDetails}
                jobData={jobData}
                FacilityData={FacilityData}
                jobId={jobId}
                jobsLoader={jobsLoader}
                specificDate={specificDate}
                shiftDetails={shiftDetails}
                isDataLoading={isDataLoading}
                sectionTitle={sectionTitle}
                scheduleSubName={scheduleSubName}
                shiftData={shiftData}
                workOrderResponseData={workOrderResponseData}
                employees={employees}
                employeeCount={employeeCount}
                scheduleStatus={scheduleStatus}
                isPrimeVendor={false}
                workOrderId={dataItem?.workOrderId}
                woScheduleMasterId={dataItem?.woScheduleMasterId ?? ''}
              />
            );
          })
        ) : (
          <NoDataTag>{t('noData:noSchedulesFound')}</NoDataTag>
        )}
      </>
    );
  };

  useEffect(() => {
    getSchedulesData();
  }, [jobData, specificDate, workOrderResponseData]);

  return (
    <>
      <DropdownWrapper sx={{ justifyContent: 'flex-end', marginTop: '2rem' }}>
        <DatePickerWrapper
          sx={{
            maxWidth: '13rem',
            '& .MuiOutlinedInput-root': {
              '& .MuiInputBase-input': {
                padding: '0.5625rem 0 0.5625rem 1rem',
                color: theme.palette.primary.dark,
              },
            },
          }}>
          <DateSelection
            requiredText={t('calendar:dateFieldIsRequired')}
            control={control}
            fieldName={'specificDate'}
            label={t('taskRejectionAndRework:selectDate')}
            isExcludePastDate={false}
            showSpecificRangeDates={{
              from: new Date(jobStartDate),
              to: new Date(jobEndDate),
            }}
            textFieldStyles={{
              '& .MuiOutlinedInput-root': {
                '& fieldset,&:hover fieldset,&.Mui-focused fieldset': {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.dark,
                  borderRadius: '1rem',
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.primary.dark,
                '&.Mui-focused': {
                  color: theme.palette.primary.dark,
                },
              },
            }}
            futureShiftDates={futureShiftDates}
          />
        </DatePickerWrapper>
      </DropdownWrapper>

      {jobsLoader || isDataLoading ? (
        <LoaderTag>
          <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
        </LoaderTag>
      ) : (
        <>{renderTasksData()}</>
      )}
    </>
  );
};

export default AssociateCard;
